import React, { Component, useEffect, useRef, useState } from 'react';
import 'styles/main.css';

import { animated, useSpring } from '@react-spring/web';
import { browserName, osName } from 'react-device-detect';
import { getAnalytics, logEvent, setUserId } from 'firebase/analytics';
import { initializeApp } from "firebase/app";
import { loadStripe } from '@stripe/stripe-js';
import moment from 'moment-timezone';
import update from 'immutability-helper';
import { useDataStore } from 'files/StateManagement.js';

import Abstract from 'classes/Abstract.js';
import API from 'files/api.js';
import{ About, Affiliates, Contact, FAQ, HomeSplash, KnowledgeBase, LeadLanding, LeadLandingUrgency, Legal, PlanOverview, Pricing, RequiemChat, RequiemChatHistory, RequiemChatPanel, SalesOps, SalesOpsLanding, ServicesDataVault, ServicesEmergencyTrigger, ServicesInstructions, ServicesLifeChecks, ServicesLinkedAccounts, ServicesRequiem } from 'managers/Components.js';
import { Accounts, AddEditUser, BrandAssetsVault, CompleteLifeCheck, Devices, DigitalLifeCheckup, EmergencyTrigger, IdentityVerification, Instructions, JitsiMeeting, LegalAgreementsLayer, Login, MyPartnerships, NewChildPlanInvitation, NewPartnershipEnrollment, Referrals, ResetPasswordConfirmation, runTrustedDeviceRegistration, SocialContent, TrustedContacts, TrustedDevices, validateTrustedDeviceToken } from 'managers/Users.js';
import AlertStack from 'views/AlertStack.js';
import { AltBadge } from 'views/Main.js';
import Appearance from 'styles/Appearance.js';
import BannerStack from 'views/BannerStack.js';
import Content from 'managers/Content.js';
import { CalloutIndex, StartIndex } from 'structure/Layer.js';
import { DataVaultPanel } from 'managers/DataVault.js';
import { EmergencyTriggers, LifeChecks, MyPayouts, NewSubscription, Payments, PaymentMethods, Subscriptions } from 'managers/Payments.js';
import Events from 'managers/Events.js';
import GlobalBanner from 'views/GlobalBanner.js';
import LargeAlert from 'views/LargeAlert.js';
import Onboarding from 'views/Onboarding.js';
import { Partnerships } from 'managers/Partnerships.js';
import QueryString from 'query-string';
import Request from 'files/Request.js';
import { SecurePortal } from 'managers/Public.js';
import Sheet from 'views/Sheet.js';
import User from 'classes/User.js';
import Utils from 'files/Utils.js';
import WebSocketManager from 'files/WebSockets.js';

const ContentManager = Content.new();
const EventsManager = Events.new();

const panels = {
    about: [{
        Component: About,
        id: 'about'
    }],
    accounts: [{
        Component: Accounts,
        id: 'accounts'
    }],
    affiliates: [{
        Component: Affiliates,
        id: 'affiliates'
    }],
    billing: [{
        Component: Subscriptions,
        id: 'subscriptions'
    },{
        Component: PaymentMethods,
        id: 'payment_methods'
    },{
        Component: Payments,
        id: 'payments'
    }],
    contact: [{
        Component: Contact,
        id: 'contact'
    }],
    data_vault: [{
        Component: DataVaultPanel,
        id: 'data_vault'
    }],
    devices: [{
        Component: Devices,
        id: 'devices'
    },{
        Component: TrustedDevices,
        id: 'trusted_devices'
    }],
    emergency_trigger: [{
        Component: EmergencyTrigger,
        id: 'emergency_trigger'
    },{
        Component: EmergencyTriggers,
        id: 'emergency_triggers'
    }],
    faq: [{
        Component: FAQ,
        id: 'faq'
    }],
    home: [{
        Component: HomeSplash,
        id: 'home'
    }],
    lead_landing: [{
        Component: LeadLanding,
        focus: 'default',
        id: 'lead_landing'
    },{
        Component: LeadLanding,
        focus: 'third_party_referral',
        id: 'lead_landing'
    },{
        Component: LeadLandingUrgency,
        focus: 'urgency',
        id: 'lead_landing_urgency'
    }],
    sales_ops_landing: [{
        Component: SalesOpsLanding,
        id: 'sales_ops_landing'
    }],
    knowledge_base: [{
        Component: KnowledgeBase,
        id: 'knowledge_base'
    }],
    legal: [{
        Component: Legal,
        id: 'legal'
    }],
    lifechecks: [{
        Component: CompleteLifeCheck,
        id: 'complete_life_check'
    }],
    lifechecks_history: [{
        Component: LifeChecks,
        id: 'life_checks'
    }],
    partnerships: [{
        Component: Partnerships,
        id: 'partnerships'
    }],
    pricing: [{
        Component: Pricing,
        id: 'pricing'
    }],
    outreach: [{
        Component: MyPayouts,
        id: 'my_payouts',
        visible: user => {
            return user?.onboarding.payouts?.enabled === true;
        }
    },{
        Component: MyPartnerships,
        id: 'my_partnerships',
        visible: user => {
            return user?.partnership_ids?.length > 0;
        }
    },{
        Component: SocialContent,
        id: 'social_content',
        visible: user => {
            return user?.level <= User.levels.get().sales_ops;
        }
    },{
        Component: Referrals,
        id: 'referrals'
    },{
        Component: BrandAssetsVault,
        id: 'brand_assets_vault',
        visible: user => {
            return user?.level <= User.levels.get().sales_ops;
        }
    }],
    plan_overview: [{
        Component: PlanOverview,
        id: 'plan_overview'
    }],
    requiem: [{
        Component: RequiemChatPanel,
        id: 'requiem_chat_landing'
    },{
        Component: RequiemChatHistory,
        id: 'requiem_chat_history'
    }],
    sales_ops: [{
        Component: SalesOps,
        id: 'sales_ops'
    }],
    secure_portal: [{
        Component: SecurePortal,
        id: 'secure_portal'
    }],
    services_data_vault: [{
        Component: ServicesDataVault,
        id: 'services_data_vault'
    }],
    services_emergency_trigger: [{
        Component: ServicesEmergencyTrigger,
        id: 'services_emergency_trigger'
    }],
    services_instructions: [{
        Component: ServicesInstructions,
        id: 'services_instructions'
    }],
    services_life_checks: [{
        Component: ServicesLifeChecks,
        id: 'services_life_checks'
    }],
    services_linked_accounts: [{
        Component: ServicesLinkedAccounts,
        id: 'services_linked_accounts'
    }],
    services_requiem_ai: [{
        Component: ServicesRequiem,
        id: 'services_requiem_ai'
    }],
    trigger_instructions: [{
        Component: Instructions,
        id: 'trigger_instructions'
    },{
        Component: TrustedContacts,
        id: 'trusted_contacts'
    }]
}

const App = () => {

    const accountElementRef = useRef(null);
    const analyticsRef = useRef(null);
    const deviceTokenRef = useRef(null);
    const headerRef = useRef(null);
    const layersRef = useRef(null);
    const requestRef = useRef({});
    const stripeRef = useRef(null);
    const userRef = useRef(null);

    const [active, setActive] = useState({ view: 'home' });
    const [accountDrawerExpanded, setAccountDrawerExpanded] = useState(false);
    const [accountDrawerAnimations, setAccountDrawerAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 26 },
        right: -350,
        opacity: 0
    }));
    const [alerts, setAlerts] = useState([]);
    const [filterAnimations, setFilterAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 26 },
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderColor: 'rgba(255,255,255,0.1)'
    }));
    const [banners, setBanners] = useState([]);
    const [contentAnimations, setContentAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 1,
        top: 0,
    }));
    const [deviceToken, setDeviceToken] = useState(null);
    const [largeAlert, setLargeAlert] = useState(null);
    const [layers, setLayers] = useState([]);
    const [layerIndex, setLayerIndex] = useState([]);
    const [logoAnimations, setLogoAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 26 },
        left: -50,
        opacity: 0
    }));
    const [menuAnimations, setMenuAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 26 },
        right: -50,
        opacity: 0
    }));
    const [menuDrawerContentAnimations, setMenuDrawerContentAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 18 },
        left: 0
    }));
    const [menuDrawerAnimations, setMenuDrawerAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 18 },
        left: window.innerWidth
    }));
    const [menuExpanded, setMenuExpanded] = useState(false);
    const [selectedNavigationItem, setSelectedNavigationItem] = useState(false);
    const [nonce, setNonce] = useState(moment().unix());
    const [sheet, setSheet] = useState(null);
    const [theme, setTheme] = useState('light');

    // declare data store variables and setters
    const { 
        onboarding, partnership, request, sockets, system, user, setAdvertisingMetadata, setOnboarding, setReferredByUserCode, setRequest, setUser, setSockets, setSystem 
    } = useDataStore();

    const onAccountsImportComplete = evt => {

        // fetch content for accounts subscribers
        utils.content.fetch('accounts');

        // show confirmation banner 
        utils.banner.show({
            icon: 'images/banner-icon-checkmark.png',
            message: `Your linked accounts import has completed. ${evt.total} accounts have linked to your profile.`,
            onClick: () => {
                utils.navigation.set({ view: 'accounts' });
            },
            title: 'Import Complete',
        });
    }

    const onActiveChange = () => {

        // no further logic is required if no view has been set
        if(!active || !active.view) {
            return;
        }

        // scroll page to top
        window.scrollTo(0,0);

        // animate changes for header background
        setFilterAnimations({
            backgroundColor: viewRequiresDarkTheme() ? 'rgba(255,255,255,0.1)' : `rgba(35,35,35,${system.window.isMobile ? 0.8 : 1})`,
            borderColor: viewRequiresDarkTheme() ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,1)'
        });

        // update theme based on the active view
        onSetStyleSheetProperties();

        // update document title
        let title = getDocumentTitleForView(active.view);
        window.document.title = title;

        // update apple touch title, icon, and manifest for pwa
        document.querySelector('link[rel="apple-touch-icon"]').setAttribute('href', active.view === 'emergency_trigger' ? '/apple-touch-emergency-trigger.png' : '/apple-touch.png');
        document.querySelector('link[rel="manifest"]').setAttribute('href', `https://life-wipe.com/api/v1/utils/?type=pwa_manifest${active.view === 'home' ? '' : `&app_route=${active.view}`}`);
        document.querySelector('meta[name="apple-mobile-web-app-title"]').setAttribute('content', active.view === 'emergency_trigger' ? 'EMERGENCY' : 'LifeWipe Inc.');

        // log page view in firebase analytics
        utils.analytics.log('page_view', {
            page_location: window.location.href,
            page_path: `/${active.view}`,
            page_title: title.replace('LifeWipe Inc: ', ''),
            view_name: active.view
        });

        // collapse mobile navigation menu if applicable
        if(Utils.isMobile() === true) {
            setMenuExpanded(false);
        }
    }

    const onActivateAccount = async token => {
        try {

            // retrieve user details from the server
            let { user } = await Request.get(utils, '/users/', {
                token: token,
                type: 'activate_account'
            });

            // determine if device needs to be registered before completing the login process
            let response = await validateTrustedDevice(user.user_id);

            // hand off login response to processor
            onLogin(user, { 
                redirect: { view: 'accounts' },
                remember_me: false,
                trusted_device_token: response.token 
            });

            // open the onboarding flow to continue account activation
            onSetOnboardingVisibility(true);

        } catch(e) {
            console.error(e.message);
        }
    }

    const onExternalPaymentProcessingComplete = async token => {
        try {

            // retrieve user details from the server
            let { user } = await Request.post(utils, '/subscriptions/', {
                token: token,
                type: 'process_external_payment_processing_completion_redirect'
            });

            // determine if device needs to be registered before completing the login process
            let response = await validateTrustedDevice(user.user_id);

            // hand off login response to processor
            onLogin(user, { 
                redirect: { view: 'accounts' },
                remember_me: false,
                trusted_device_token: response.token 
            });

            // open new subscription layer with default view set to completion confirmation
            utils.layer.open({
                Component: NewSubscription.bind(this, {
                    defaultView: 'subscription_confirmed'
                }),
                id: 'new_subscription'
            });

        } catch(e) {
            console.error(e.message);
        }
    }

    const onAddTriggerToHomeScreen = () => {

        // prepare tokens for authentication url
        let loginToken = localStorage.getItem('user.refresh_token');
        let trustedDeviceToken = utils.user.device.get(user?.user_id);

        // open window for pwa install page and pass along tokens for home screen bookmark
        window.open(`https://sos.life-wipe.com/pwa/install/?login_token=${loginToken}&trusted_device_token=${trustedDeviceToken}`);
    }

    const onCloseLayer = layerID => {

        // filter layer id out of layer indexes
        setLayerIndex(layerIndex => {
            return update(layerIndex, {
                $apply: ids => ids.filter(id => id !== layerID)
            });
        });

        setLayers(layers => {

            // update layer and set visible flag to false
            let next = update(layers, {
                $apply: layers => layers.map(layer => {
                    if(layer.id === layerID) {
                        layer.visible = false;
                    }
                    return layer;
                })
            });

            // determine if at least one layer is still visible
            let targets = next.filter(layer => layer.visible !== false);
            if(targets.length === 0) {
                console.log('layers reset');
                document.body.style.overflowY = 'scroll';
                return [];
            }
            return next;
        });
    }

    const onConnectToUserSockets = () => {

        // submit request to connect to user sockets
        sockets.connect({ user, url: API.sockets });

        // add connection listener for accounts namespace
        sockets.on('accounts', 'connect', () => {
            console.log('[websockets]: subscribing to accounts namespace');
            sockets.on('accounts', 'import_complete', onAccountsImportComplete);
        });

        // add connection listener for requiem namespace
        sockets.on('requiem', 'connect', () => {
            if(userRef.current) {
                console.log('[websockets]: subscribing to requiem namespace');
                sockets.emit('requiem', 'join', { user_id: userRef.current.user_id });
            }
        });

        // add disconnection listener for accounts namespace
        sockets.on('accounts', 'disconnect', () => {
            console.log('[websockets]: unsubscribing from accounts namespace');
            sockets.off('accounts', 'import_complete', onAccountsImportComplete);
        });
    }

    const onEmergencyOverrideClick = () => {
        utils.alert.show({
            title: 'Emergency Override',
            message: `To stop your instructions from being carried out, we need to initiate an override check. This means a new LifeCheck will be sent—you must complete it to confirm your identity and cancel the process. No instructions will be paused until verification is successful.`,
            buttons: [{
                key: 'confirm',
                title: 'Continue',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onSendEmergencyOverrideCheck, 500);
                    return
                }
            }
        });
    }

    const onIdentityVerificationComplete = () => {
        utils.large_alert.show({
            buttons: [{
                color: 'grey',
                key: 'close',
                onClick: () => {},
                text: 'Exit and Get Started'
            }],
            description: `Welcome back — your identity has been successfully verified. You're now fully activated and ready to secure your digital legacy. LifeWipe’s features — like remote wipes, emergency triggers, and posthumous data deletion — involve serious, irreversible actions. Verification ensures only you (or your pre-approved contacts) can activate them.`,
            items: [{
                description: `Choose how often we check in to confirm you're okay. If you stop responding, LifeWipe can begin protective actions — like locking your devices, alerting trusted contacts, or wiping data after a delay.`,
                onClick: utils.navigation.set.bind(this, { view: 'lifechecks_history' }),
                title: 'Set Up LifeChecks'
            },{
                description: `Turn on your manual panic button — built for high-risk moments. If you're in immediate danger or lose access to your device, trigger an instant remote wipe with a tap, SMS code, or secure web link.`,
                onClick: utils.navigation.set.bind(this, { view: 'emergency_trigger' }),
                title: 'Enable Emergency Trigger'
            },{
                description: `Secure your broader digital life. Connect cloud storage, email, crypto wallets, or other platforms you want included in your emergency protocols — for deletion, message delivery, or posthumous handling.`,
                onClick: utils.navigation.set.bind(this, { view: 'accounts' }),
                title: 'Link External Accounts'
            }],
            title: 'Identity Verified'
        });
    }

    const onLegalClick = key => {
        setNextActive({ 
            view: 'legal', 
            waypoint: key 
        });
    }

    const onSendEmergencyOverrideCheck = async () => {
        try {
            
            // submit request to server to initiate a new emergency override
            await Request.post(utils, '/users/', {
                type: 'new_emergency_override'
            });

            // show confirmation alert that override has been sent
            utils.alert.show({
                title: 'Override Initiated',
                message: `We've sent a new LifeCheck to confirm your identity. To cancel your active instructions, you must complete this verification within the next 60 minutes. If the LifeCheck isn't completed in time, your original directives will continue as scheduled.`
            });

        } catch(e) {
            utils.alert.error({
                error: e,
                message: `There was an issue initiating your emergency override. ${e.message || 'An unknown error occurred'}`,
                origin: 'root.on_send_emergency_override_check'
            });
        }
    }

    const onJoinMeetingRequest = id => {

        const onConfim = () => {
            utils.layer.open({
                Component: JitsiMeeting.bind(this, { meeting_id: id }),
                id: `jitsi_meeting_${id}`
            });
        }

        // no further logic is required if no id was provided
        if(!id) {
            return;
        }

        // show alert with options to sign in or join as guest for meeting access
        utils.alert.show({
            title: 'Join Meeting',
            message: 'Sign in for a personalized experience, or continue as a guest to join your Life Wipe specialist now.',
            buttons: [{
                key: 'signin',
                title: 'Sign-In',
                style: 'default'
            },{
                key: 'guest',
                title: 'Continue as Guest',
                style: 'cancel'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key !== 'cancel') {
                    key === 'guest' ? onConfim() : utils.user.request.login({}, onConfim);
                    return;
                }
            }
        })
    }

    const onLayerReposition = ({ id, position }) => {
        return;
        let index = layers.findIndex(layer => id === layer.id);
        if(index < 0) {
            console.log('no layer index');
            return;
        }
        setLayers(layers => {
            return update(layers, {
                [index]: {
                    position: {
                        $set: position
                    }
                }
            });
        });
    }

    const onLogin = async (user, options = {}) => {

        // write login token to local storage
        if(options.remember_me === true) {
            localStorage.setItem('user.refresh_token', user.refresh_token);
        }

        // update local storage and state with trusted device token for future requests
        utils.user.device.set(user.user_id, options.trusted_device_token);

        // update local state with user object and determine if an action was requested through query parameters and should be executed post-login
        let target = User.create(user);
        userRef.current = target;
        setUser(userRef.current);

        // determine if a redirect was requested
        if(options.redirect) {
            setNextActive(options.redirect);
        }
    }

    const onLoginClick = () => {
        
        // hide navigation menu for mobile devices
        if(Utils.isMobile() === true) {
            setMenuExpanded(false);
            setSelectedNavigationItem(null);
        }
        presentLoginWindow();
    }

    const onLoginWithRefreshToken = async token => {
        try {

            // submit login request to server
            let { user } = await Request.post(utils, '/users/', {
                refresh_token: token,
                type: 'login'
            });

            // log firebase login event
            utils.analytics.log('login', { method: 'refresh_token' });

            // determine if device needs to be registered before completing the login process
            let response = await validateTrustedDevice(user.user_id);

            // hand off login response to processor
            onLogin(user, { 
                remember_me: true,
                trusted_device_token: response.token 
            });

        } catch(e) {

            // remove refresh token from local storage to prevent future login attempts
            localStorage.removeItem('user.refresh_token');

            // remove trusted device token from local storage if error indicates that device is no longer trusted, no error alert is needed here since the login flow is behind the scene for refresh tokens
            if(e.code === Request.status.get().invalid_trusted_device_token) {
                utils.user.device.remove_current_token();
                return;
            }

            // show error alert
            utils.alert.show({
                error: e,
                message: `There was an issue logging into your account. ${e.message || 'An unknown error occurred'}`,
                title: 'Oops!'
            });
        }
    }

    const onMouseEnterMainContent = () => {

        // deselect currently selected navigation item some menu will hide
        setSelectedNavigationItem(false);
    }

    const onOpenLayer = nextLayer => {

        // no further logic is required if the layer is already open
        if(layers.find(layer => layer.id === nextLayer.id && layer.visible !== false)) {
            return;
        }

        setTimeout(() => {
            setLayers(layers => update(layers, {
                $push: [nextLayer]
            }))
            setLayerIndex(layerIndex => update(layerIndex, {
                $push: [nextLayer.id]
            }))
        }, 0)
    }

    const onProcessChildPlanInvitation = ({ parent_plan, token }) => {
        utils.layer.open({
            Component: NewChildPlanInvitation.bind(this, { parent_plan, token }),
            id: 'new_child_plan_invitation',
        });
    }

    const onProcessInvitationRequest = async token => {
        try {

            // retrieve resources from invitation token from server
            let response = await Request.get(utils, '/users/', {
                token: token,
                type: 'invitation_resources'
            });

            // offload response to sub-processor
            switch(response.channel) {
                case 'new_child_plan':
                onProcessChildPlanInvitation({ ...response, token });
                break;

                default:
                throw new Error('Unsupported invitation channel');
            }

        } catch(e) {
            utils.alert.show({
                error: e,
                message: `There was an issue processing your invitation. ${e.message || 'An unknown error occurred'}`,
                title: 'Oops!'
            });
        }
    }

    const onProcessSalesModeRequest = () => {

        const onConfirm = user => {

            // determine if user account is able to access sales mode
            if(!user || user.level > User.levels.get().sales_ops) {
                utils.alert.show({
                    title: 'Access Denied',
                    message: 'Your account does not have permission to access this content.'
                });
                return;
            }

            // update request ref with sales mode flag and set next view
            requestRef.current = { mode: 'sales' };
            setRequest(requestRef.current);
            setNextActive({ view: 'sales_ops_landing' });
        }

        user ? onConfirm(user) : utils.user.request.login({}, onConfirm);
    }

    const onProcessSecurePaymentRequest = async code => {
        //http://localhost:3000/?action=secure_payment_request&code=1.69NSUvSUFvl
        utils.layer.open({
            Component: NewSubscription.bind(this, {
                paymentRequest: code
            }),
            id: 'new_subscription'
        });
    }

    const onReviewLegalAgreements = async token => {
        try {

            const onConfirm = async legalAgreements => {
                return new Promise(async (resolve, reject) => {
                    try {

                        // submit request to server to send new sms opt in message
                        await Request.post(utils, '/partnerships/', {
                            legal_agreements: legalAgreements,
                            token: token,
                            type: 'update_legal_agreements_public'
                        });

                        resolve({ 
                            message: `Your responses have been recorded and a confirmation email has been sent to the email address on file with your account.` 
                        });

                    } catch(e) {
                        reject(e);
                    }
                });
            }

            // retrieve legal agreements from server
            let { document_ids } = await Request.get(utils, '/partnerships/', {
                token: token,
                type: 'verify_legal_agreements_token'
            });

            // open layer to review legal agreements
            utils.layer.open({
                Component: LegalAgreementsLayer.bind(this, { 
                    documentIDs: document_ids,
                    onAsyncComplete: onConfirm
                }),
                id: 'legal_agreements'
            });

        } catch(e) {
            utils.alert.show({
                error: e,
                message: `There was an issue preparing your legal agreements for review. ${e.message || 'An unknown error occurred'}`,
                title: 'Oops!'
            });
        }
    }

    const onRequestLogout = () => {
        utils.alert.show({
            title: 'Logout',
            message: 'Are you sure that you want to logout of your account?',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(processLogoutRequest, 500);
                    return;
                }
            }
        });
    }
    
    const onRequestSecureAuth = async token => {
        try {

            // submit request to verify and decode token
            let { origin, origin_formatted, private_token } = await Request.get(utils, '/users/', {
                token: token,
                type: 'validate_secure_auth_token'
            });

            // determine if login was requested and a user is already signed in
            if(user) {
                utils.alert.show({
                    title: 'Secure Authentication',
                    message: `A request to securely connect with your account was made from ${origin_formatted}. Do you want to proceed as ${user.full_name}?`,
                    buttons: [{
                        key: 'confirm',
                        title: 'Yes',
                        style: 'default'
                    },{
                        key: 'another_user',
                        title: 'Use Other Account',
                        style: 'default',
                    },{
                        key: 'cancel',
                        title: 'Cancel',
                        style: 'cancel'
                    }],
                    onClick: key => {
                        setTimeout(() => {
                            if(key === 'confirm') {
                                processSecureAuthRequest({ 
                                    access_token: user?.token, 
                                    origin_formatted, 
                                    private_token 
                                });
                                return;
                            }
                            if(key === 'another_user') {
                                processLogoutRequest({ redirect: false });
                                processSecureAuthRequest({ origin_formatted, private_token });
                            }
                        }, 500);
                    }
                });
                return;
            }

            // show alert requesting confirmation from user for secure auth
            utils.alert.show({
                title: 'Secure Authentication',
                message: `A request to securely connect with your account was made from ${origin_formatted}. How do you want to proceed?`,
                buttons: [{
                    key: 'login',
                    title: 'Login',
                    style: 'default'
                },{
                    key: 'signup',
                    title: 'Signup',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'login' || key === 'signup') {
                        processSecureAuthRequest({ origin, private_token, route: key });
                        return;
                    }
                }
            });

        } catch(e) {
            utils.alert.show({
                error: e,
                message: `There was an issue preparing your secure authorization request. ${e.message || 'An unknown error occurred'}`,
                title: 'Oops!'
            });
        }
    }

    const onRunGeneralQueryActions = () => {

        const { action, id, token } = requestRef.current;

        // determine if an action was requested through query parameters and should be executed during launch
        switch(action) {
            case '403':
            utils.alert.show({
                title: 'Access Denied',
                message: 'You do not have permission to access this content.'
            });
            break;

            case 'activate_account':
            onActivateAccount(token);   
            return { shouldAutoLogin: false };
        
            case 'external_payment_processing_complete':
            onExternalPaymentProcessingComplete(token);
            return { shouldAutoLogin: false };

            case 'join_meeting':
            onJoinMeetingRequest(id);
            break;

            case 'reset_password':
            utils.layer.open({
                Component: ResetPasswordConfirmation.bind(this, { token }),
                id: 'reset_password_confirmation'
            });
            break;

            case 'review_legal_agreements':
            onReviewLegalAgreements(token);
            break;

            default:
            return;
        }

        // update request state to exclude action now that it has ran
        requestRef.current = { 
            ...requestRef.current, 
            action: null,
            previous_action: requestRef.current.action
        };
    }

    const onRunUserQueryActions = () => {

        let { action, alias, code, destination, route, token, utm_campaign, utm_medium, utm_source } = requestRef.current;
        const whitelistedRoutes = ['accounts','billing','data_vault','emergency_trigger','trigger_instructions','lifechecks_history','outreach','plan_overview','requiem'];

        // determine if draft outreach campaign tracking parameters are present
        if(utm_campaign === 'draft_outreach') {
            
            // prepare callback for login
            let onConfirm = async () => {
                try {

                    // submit request to capture click event
                    await Request.post(utils, '/users/', {
                        type: 'track_draft_outreach_interaction',
                        utm_campaign: utm_campaign,
                        utm_medium: utm_medium,
                        utm_source: utm_source
                    });

                    switch(destination) {
                        case 'accounts':
                        setNextActive({ view: 'accounts' });
                        break;

                        case 'plan_overview':
                        setNextActive({ view: 'plan_overview' });
                        break;
                    }

                } catch(e) {
                    console.error(e.message);
                }
            }
            user ? onConfirm() : utils.user.request.login({}, onConfirm);
            return;
        }

        // determine if route requires a user validation 
        switch(route) {
            case 'sales_ops_landing':
            onProcessSalesModeRequest();
            break;
        }

        // determine if an action was requested through query parameters and should be executed once a user is available
        switch(action) {
            case 'checkup_quiz':
            onShowCheckupQuiz();
            break;

            case 'cyber_finders_application':
            case 'sales_ops_application':
            user ? setRequest(requestRef.current) : utils.user.request.login({}, setRequest.bind(this, requestRef.current));
            return;

            case 'identity_verification_complete':
            user ? onIdentityVerificationComplete() : utils.user.request.login({}, onIdentityVerificationComplete);
            break;

            case 'invitation':
            setOnboarding({ ...onboarding, enabled: false });
            onProcessInvitationRequest(token);
            break;

            case 'partner_user_enrollment':
            onShowPartnerUserEnrollment(alias);
            break;

            case 'secure_auth':
            onRequestSecureAuth(token);
            break;

            case 'secure_payment_request':
            user ? onProcessSecurePaymentRequest(code) : utils.user.request.login({}, onProcessSecurePaymentRequest.bind(this, code));
            break;

            case 'upgrade':
            setNextActive({ view: 'pricing' });
            onShowSubscriptionOptions();
            break;

            default:
            if(user && whitelistedRoutes.includes(route) === true) {
                setNextActive({ view: route });
            }
            return;
        }

        // update request state to exclude action now that it has ran
        setRequest({ 
            ...requestRef.current, 
            action: null
        });
    }

    const onShowCheckupQuiz = () => {
        utils.layer.open({
            Component: DigitalLifeCheckup,
            id: 'digital_life_checkup'
        });
    }

    const onShowPartnerUserEnrollment = alias => {

        // https://localhost:3000/?action=partner_user_enrollment&alias=usmobile1
        const onConfirm = () => {

            // navigate to the outreach view
            utils.navigation.set({ view: 'outreach'})

            // open layer to enroll in partnership
            utils.layer.open({
                Component: NewPartnershipEnrollment.bind(this, { alias }),
                id: 'new_partnership_enrollment'
            });
        }
        user ? onConfirm() : utils.user.request.login({}, onConfirm);
    }

    const onShowSubscriptionOptions = () => {
        utils.layer.open({
            Component: NewSubscription.bind(this, {
                freeTrial: true
            }),
            id: 'new_subscription'
        });
    }

    const onSetAccountDrawerExpandedValue = val => {
        setAccountDrawerAnimations({
            to: async animate => {
                if(val === true) {
                    setAccountDrawerExpanded(val);
                }
                await animate({
                    opacity: val ? 1 : 0,
                    right: val ? 16 : -350
                });
                if(val === false) {
                    setAccountDrawerExpanded(val);
                }
            }
        });
    }

    const onSelectedNavigationItemChange = () => {
        if(selectedNavigationItem === null) {
            setMenuDrawerContentAnimations({ left: 0 });
            return;
        }
        if(menuExpanded === true) {
            setMenuDrawerContentAnimations({ left: -system.window.size.width });
        }
        if(menuExpanded === false) {
            setSelectedNavigationItem(null);
        }
    }

    const onSetLayerIndex = layerID => {

        let index = layers.findIndex(layer => layer.id === layerID);
        if(index < 0) {
            console.log('no layer index');
            return;
        }
        setLayers(layers, update(layers, {
            $apply: layers => layers.map(layer => {
                layer.moveToFront = layer.id === layerID;
                return layer;
            })
        }))

        let _index = layerIndex.findIndex(id => id === layerID);
        setLayerIndex(layerIndex, update(layerIndex, {
            $splice: [
                [_index, 1],
                [0, 0, layerID]
            ]
        }))
    }

    const onSetOnboardingVisibility = val => {
        setOnboarding(props => {
            return update(props, {
                visible: {
                    $set: props.enabled ? val : false
                }
            });
        });
    }

    const onSetStyleSheetProperties = () => {

        // update window variable and local state for theme
        window.theme = viewRequiresDarkTheme() || window.matchMedia('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
        setTheme(window.theme);

        // update css values for theme colors
        document.body.className = window.theme;
        document.documentElement.style.setProperty('--theme', window.theme);
        document.documentElement.style.setProperty('--text', Appearance.colors.text());
        document.documentElement.style.setProperty('--textfield', Appearance.colors.textField());
        document.documentElement.style.setProperty('--soft_border', Appearance.colors.softBorder());
        document.querySelector('meta[name="theme-color"]').setAttribute("content", Appearance.colors.layerBackground());
    }

    const onToggleMobileMenu = () => {
        setMenuExpanded(val => !val);
        setSelectedNavigationItem(null);
    }

    const onUpdateTheme = evt => {
        window.theme = evt.matches || viewRequiresDarkTheme() ? 'dark' : 'light';
        onSetStyleSheetProperties();
        setNonce(moment().unix());
    }

    const onUpdateWindowSize = () => {
        setSystem(system => {
            return update(system, {
                window: {
                    isMobile: {
                        $set: Utils.isMobile()
                    },
                    size: {
                        $set: {
                            height: window.innerHeight, 
                            width: window.innerWidth 
                        }
                    }
                }
            });
        });
    }

    const onVerifyIdentity = () => {
        utils.layer.open({
            Component: IdentityVerification.bind(this, {}),
            id: 'identity_verification'
        });
    }

    const onViewProfile = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: user,
                type: 'users'
            }),
            Component: AddEditUser.bind(this, {
                isNewTarget: false
            }),
            id: `edit_user_${user.user_id}`
        });
    }

    const getAccountComponents = () => {
        if(user) {
            return (
                <div 
                ref={accountElementRef}
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    position: 'relative'
                }}>
                    <img 
                    className={'text-button'}
                    onClick={onSetAccountDrawerExpandedValue.bind(this, !accountDrawerExpanded)}
                    src={`images/navigation-icon-dark.png`}
                    style={{
                        height: 30,
                        marginLeft: 12,
                        objectFit: 'contain',
                        position: 'relative',
                        width: 30
                    }} />
                </div>
            )
        }
        return (
            <>
            <span 
            className={'text-button'}
            onClick={onLoginClick}
            style={{
                ...Appearance.textStyles.title(),
                color: 'white',
                fontFamily: 'Zen Dots',
                marginRight: 32
            }}>{'Login'.toUpperCase()}</span>
            <span 
            className={'text-button'}
            onClick={utils.user.request.signup.bind(this, {})}
            style={{
                ...Appearance.textStyles.title(),
                color: 'white',
                fontFamily: 'Zen Dots',
                marginRight: 12
            }}>{'Sign Up'.toUpperCase()}</span>
            </>
        )
    }

    const getDocumentTitleForView = view => {
        switch(view) {
            case 'faq':
            return 'LifeWipe Inc: Frequently Asked Questions';

            case 'lead_landing':
            case 'lead_landing_urgency':
            return 'LifeWipe Inc: Digital Legacy & Data Protection Services';

            case 'lifechecks':
            return 'LifeWipe Inc: LifeCheck Verification';
            
            case 'lifechecks_history':
            return 'LifeWipe Inc: LifeChecks';

            case 'sales_ops_landing':
            return 'LifeWipe Inc: Sales';

            case 'services_data_vault':
            return 'LifeWipe Inc: Secure Data Vault';

            case 'services_emergency_trigger':
            return 'LifeWipe Inc: Emergency Trigger';

            case 'services_instructions':
            return 'LifeWipe Inc: Instructions';

            case 'services_life_checks':
            return 'LifeWipe Inc: LifeChecks';

            case 'services_linked_accounts':
            return 'LifeWipe Inc: Linked Accounts';

            case 'services_requiem_ai':
            return 'LifeWipe Inc: Requiem AI';

            default:
            return view === 'home' ? 'LifeWipe Inc.' : `LifeWipe Inc: ${Utils.ucWords(view.replace('_', ' '))}`
        }
    }

    const getUserItems = () => {

        let theme = 'dark'; // window.theme === 'dark' ? 'dark' : 'light';
        return [{
            icon: `images/user-navigation-payments-${active.view === 'billing' ? 'selected-' : ''}${theme}.png`,
            key: 'billing',
            title: 'Billing'
        },{
            icon: `images/user-navigation-devices-${active.view === 'devices' ? 'selected-' : ''}${theme}.png`,
            key: 'devices',
            title: 'Devices'
        },{
            icon: `images/user-navigation-emergency-trigger-${active.view === 'emergency_trigger' ? 'selected-' : ''}${theme}.png`,
            key: 'emergency_trigger',
            title: Utils.isMobile() ? 'Emergency' : 'Emergency Trigger'
        },{
            icon: `images/user-navigation-instructions-${active.view === 'trigger_instructions' ? 'selected-' : ''}${theme}.png`,
            key: 'trigger_instructions',
            title: 'Instructions'
        },{
            icon: `images/user-navigation-subscriptions-${active.view === 'lifechecks_history' ? 'selected-' : ''}${theme}.png`,
            key: 'lifechecks_history',
            title: 'LifeChecks'
        },{
            icon: `images/user-navigation-accounts-${active.view === 'accounts' ? 'selected-' : ''}${theme}.png`,
            key: 'accounts',
            title: 'Locket Passwords'
        },{
            icon: `images/user-navigation-referrals-${active.view === 'outreach' ? 'selected-' : ''}${theme}.png`,
            key: 'outreach',
            title: 'Outreach'
        },{
            icon: `images/user-navigation-payments-${active.view === 'profile' ? 'selected-' : ''}${theme}.png`,
            key: 'profile',
            title: 'Profile'
        },{
            icon: `images/user-navigation-requiem-${active.view === 'requiem' ? 'selected-' : ''}${theme}.png`,
            key: 'requiem',
            title: 'Requiem AI'
        },{
            icon: `images/user-navigation-secure-data-vault-${active.view === 'data_vault' ? 'selected-' : ''}${theme}.png`,
            key: 'data_vault',
            title: 'Secure Data Vault'
        },{
            icon: `images/user-navigation-logout-${theme}.png`,
            key: 'logout',
            title: 'Logout'
        }].map(item => ({
            ...item,
            onClick: () => {
               
                // close navigation for mobile and desktop devices
                Utils.isMobile() === true ? setMenuExpanded(false) : onSetAccountDrawerExpandedValue(false);

                // determine next steps using item key
                switch(item.key) {
                    case 'logout':
                    onRequestLogout();
                    break;

                    case 'profile':
                    onViewProfile();
                    break;

                    default:
                    setNextActive({ view: item.key });
                }
            }
        }));
    }

    const getDesktopNavigationMenu = () => {
        return user?.user_id && (
            <animated.div style={{
                alignItems: 'flex-start',
                backdropFilter: 'blur(40px)',
                backgroundColor: filterAnimations.backgroundColor,
                borderColor: filterAnimations.borderColor,
                borderRadius: 12,
                borderStyle: 'solid',
                borderWidth: 2,
                display: accountDrawerExpanded === true ? 'flex' : 'none',
                flexDirection: 'column',
                minWidth: 350,
                overflow: 'hidden',
                pointerEvents: 'auto',
                position: 'absolute',
                top: 104,
                textAlign: 'left',
                WebkitBackdropFilter: 'blur(40px)',
                zIndex: 9000,
                ...accountDrawerAnimations
            }}>
                <div style={{
                    alignItems: 'flex-start',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 24,
                    width: '100%'
                }}>
                    <div style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%'
                    }}>
                        <div style={{
                            alignItems: 'flex-start',
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1
                        }}>
                            <span style={{
                                color: 'white',
                                fontFamily: 'Zen Dots',
                                fontSize: 18,
                                whiteSpace: 'nowrap'
                            }}>{user.full_name.toUpperCase()}</span>
                            <span style={{
                                ...Appearance.textStyles.defaultText(),
                                color: 'white'
                            }}>{user.email_address}</span>
                        </div>
                        {user.subscription?.tier && (
                            <AltBadge 
                            content={{
                                color: user.subscription.tier?.color,
                                text: user.subscription.tier?.title
                            }} 
                            style={{
                                marginLeft: 36,
                                marginRight: 0 
                            }} />
                        )}
                    </div>
                </div>
                {getUserItems().map((item, index) => {
                    return (
                        <div 
                        className={'view-entry-transparent'}
                        key={index}
                        onClick={item.onClick}
                        style={{
                            alignItems: 'center',
                            borderTop: `1px solid ${Appearance.colors.divider()}`,
                            display: 'flex',
                            flexDirection: 'row',
                            padding: 12,
                            width: '100%'
                        }}>
                            <img 
                            src={item.icon}
                            style={{
                                height: 35,
                                marginRight: 12,
                                width: 35
                            }} />
                            <span style={{
                                color: 'white',
                                fontFamily: 'Zen Dots',
                                fontSize: 14,
                                flexGrow: 1,
                                ...item.key === active.view && {
                                    color: item.key === 'emergency_trigger' ? Appearance.colors.red : Appearance.colors.primary()
                                }
                            }}>{item.title.toUpperCase()}</span>
                            <img
                            src={'images/next-arrow-white-small.png'}
                            style={{
                                height: 12,
                                marginLeft: 8,
                                objectFit: 'contain',
                                opacity: 1,
                                width: 12
                            }} />
                        </div>
                    )
                })}
            </animated.div>
        )
    }

    const getDesktopVersionDetails = () => {
        return (
            <div style={{
                paddingTop: 24,
                paddingBottom: 128
            }}>
                {getFooterComponents()}
            </div>
        )
    }

    const getFooterComponents = () => {

        let isDarkMode = viewRequiresDarkTheme() || system.window.isMobile === true;
        return (
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'center'
            }}>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    color: isDarkMode ? 'white' : Appearance.colors.text()
                }}>{`© 2025 LifeWipe Inc. All rights reserved.`}</span>
                <div style={{
                    display: 'flex',
                    flexDirection: 'row'
                }}>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        color: isDarkMode ? 'white' : Appearance.colors.text()
                    }}>
                        <span 
                        className={'text-button'}
                        onClick={onLegalClick.bind(this, 'privacy_policy')}
                        style={{
                            textDecoration: 'underline'
                        }}>{`Privacy Policy`}</span>
                        {` and `}
                        <span 
                        className={'text-button'}
                        onClick={onLegalClick.bind(this, 'terms_of_service')}
                        style={{
                            textDecoration: 'underline'
                        }}>{`Terms of Service`}</span>
                    </span>
                </div>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    marginTop: 12
                }}>{`Version ${API.app_version} Build ${API.build}`}</span>
            </div>
        )
    }

    const getGlobalBanners = () => {

        let viewSupportsEmergencyOverrideBanner = active?.view !== 'lifechecks';

        // determine if system is waiting on a response from trusted contacts
        if(viewSupportsEmergencyOverrideBanner && user?.status === User.status.get().awaiting_trusted_contact) {
            return (
                <GlobalBanner 
                color={Appearance.colors.red}
                style={{
                    alignSelf: 'center'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        color: 'white',
                        fontWeight: 600
                    }}>{`Your account is in Emergency State—Trusted Contacts have been notified and we're awaiting their response. Complete the `}
                        <span 
                        className={'text-button'}
                        onClick={onEmergencyOverrideClick}
                        style={{
                            textDecoration: 'underline'
                        }}>{'Emergency Override'}</span>
                        {` to stop any further actions.`}
                    </span>
                </GlobalBanner>
            )
        }

        // determine if system is actively wiping content from account
        if(viewSupportsEmergencyOverrideBanner && [User.status.get().failed_lifecheck_verification, User.status.get().instructions_execution_granted].includes(user?.status)) {
            return (
                <GlobalBanner 
                color={Appearance.colors.red} 
                style={{
                    alignSelf: 'center'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        color: 'white',
                        fontWeight: 600
                    }}>{`Your account is in Emergency State, and your instructions are actively being executed—complete the `}
                        <span 
                        className={'text-button'}
                        onClick={onEmergencyOverrideClick}
                        style={{
                            textDecoration: 'underline'
                        }}>{'Emergency Override'}</span>
                        {` to stop any further actions.`}
                    </span>
                </GlobalBanner>
            )
        }

        // determine if onboarding is incomplete
        if(user && user.onboarding?.completed === false) {
            return (
                <GlobalBanner 
                color={'#F5B44F'}
                icon={window.theme === 'dark' ? 'images/warning-icon-white.png' : 'images/warning-icon-black.png'}
                style={{
                    alignSelf: 'center'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        fontWeight: 600
                    }}>{`Almost there! `}
                        <span 
                        className={'text-button'}
                        onClick={onSetOnboardingVisibility.bind(this, true)}
                        style={{
                            textDecoration: 'underline'
                        }}>{'Click Here'}</span>
                        {` to complete your account setup and secure your digital legacy.`}
                    </span>
                </GlobalBanner>
            )
        }

        // show a pwa banner if the device is mobile and the view is emergency_trigger
        if(Utils.isMobile() === true && active?.view === 'emergency_trigger' && !window.navigator.standalone) {
            return (
                <GlobalBanner 
                color={Appearance.colors.black}
                style={{
                    alignSelf: 'center',
                    backgroundColor: `rgba(35,35,35,${system.window.isMobile ? 0.8 : 1})`
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        color: 'white',
                        fontWeight: 600
                    }}>{`Add Emergency Trigger to your Home Screen for instant access when seconds matter. `}
                        <span 
                        className={'text-button'}
                        onClick={onAddTriggerToHomeScreen}
                        style={{
                            textDecoration: 'underline'
                        }}>{'Tap to get started.'}</span>
                    </span>
                </GlobalBanner>
            )
        }
    }

    const getLayers = () => {

        let visibleLayers = layers.filter(layer => layer.visible !== false);
        return layers.map(({ id, abstract, options, title, visible, Component }, index) => {

            if(visible === false) {
                // must return null instead of using filter
                // using filter for visible does not preseve other visible layers
                return null;
            }

            return (
                <Component
                abstract={abstract}
                key={index}
                index={index}
                title={title}
                utils={utils}
                options={{
                    ...options,
                    index: index,
                    onClose: onCloseLayer,
                    onReposition: onLayerReposition,
                    onSetLayerIndex: onSetLayerIndex,
                    totalLayersCount: visibleLayers.length,
                    zIndex: StartIndex + layerIndex.findIndex(indexID => id === indexID)
                }}/>
            )
        });
    }

    const getHeader = () => {

        // prepare naivgation items
        let items = [{
            key: 'pricing',
            onClick: setNextActive.bind(this, { view: 'pricing' }),
            title: 'Pricing'
        },{
            key: 'resources',
            title: 'Resources'
        },{
            key: 'services',
            title: 'Services'
        },{
            key: 'about',
            onClick: setNextActive.bind(this, { view: 'about' }),
            title: 'Team'
        },{
            key: 'contact',
            onClick: setNextActive.bind(this, { view: 'contact' }),
            title: 'Contact'
        },{
            key: 'user',
            title: 'My Account'
        }];

        // determine if current device is a mobile device
        if(Utils.isMobile() === true) {
            return (
                <div 
                ref={headerRef}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 16,
                    position: 'fixed',
                    width: '100vw',
                    zIndex: CalloutIndex
                }}>
                    <animated.div style={{
                        alignItems: 'center',
                        backdropFilter: 'blur(40px)',
                        borderRadius: 12,
                        borderStyle: 'solid',
                        borderWidth: 2,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        padding: 20,
                        position: 'relative',
                        textAlign: 'center',
                        WebkitBackdropFilter: 'blur(40px)',
                        width: '100%',
                        ...filterAnimations
                    }}>
                        <animated.div 
                        className={'text-button'}
                        onClick={setNextActive.bind(this, { view: 'home' })}
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                            paddingLeft: 8,
                            position: 'relative',
                            ...logoAnimations
                        }}>
                            <img 
                            src={'images/logo-text-white.png'}
                            style={{
                                height: 20,
                                width: 'auto'
                            }} />
                        </animated.div>
                        <animated.img 
                        className={'text-button'}
                        onClick={onToggleMobileMenu}
                        src={`images/navigation-icon-dark.png`}
                        style={{
                            height: 30,
                            objectFit: 'contain',
                            position: 'relative',
                            width: 30,
                            ...menuAnimations
                        }} />
                    </animated.div>
                    {getGlobalBanners()}
                    <animated.div style={{
                        backgroundColor: '#232323',
                        height: '100dvh',
                        overflow: 'hidden',
                        position: 'fixed',
                        top: 0,
                        width: '100%',
                        zIndex: CalloutIndex + 10,
                        ...menuDrawerAnimations
                    }}>
                        <animated.div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'row',
                            height: '100dvh',
                            overflow: 'hidden',
                            position: 'relative',
                            width: system.window.size.width * 2,
                            ...menuDrawerContentAnimations
                        }}>
                            <div style={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-around',
                                height: '100%',
                                padding: `20vh 20px 25vh 20px`,
                                width: system.window.size.width
                            }}>
                                {items.map((item, index) => {
                                    if(!user && item.key === 'user') {
                                        return (
                                            <span 
                                            className={'text-button'}
                                            key={index}
                                            onClick={onLoginClick}
                                            style={{
                                                ...Appearance.textStyles.layerItemTitle(),
                                                color: 'white',
                                                fontFamily: 'Zen Dots',
                                                fontSize: 24
                                            }}>{'Login'.toUpperCase()}</span>
                                        )
                                    }
                                    return (
                                        <span 
                                        className={'text-button'}
                                        key={index}
                                        onClick={evt => {
                                            if(typeof(item.onClick) === 'function') {
                                                item.onClick();
                                                return;
                                            }
                                            setSelectedNavigationItem({
                                                key: item.key,
                                                target: evt.target
                                            });
                                        }}
                                        style={{
                                            ...Appearance.textStyles.layerItemTitle(),
                                            color: 'white',
                                            fontFamily: 'Zen Dots',
                                            fontSize: 24,
                                            ...item.key === active.view && {
                                                color: Appearance.colors.primary()
                                            }
                                        }}>{item.title.toUpperCase()}</span>
                                    )
                                })}
                            </div>
                            <div style={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-around',
                                height: '100%',
                                padding: `15vh 20px 20vh 20px`,
                                position: 'relative',
                                width: system.window.size.width
                            }}>
                                {getNavigationComponents()}
                                <img
                                className={'text-button'}
                                onClick={setSelectedNavigationItem.bind(this, null)}
                                src={`images/back-arrow-dark.png`}
                                style={{
                                    height: 25,
                                    objectFit: 'contain',
                                    position: 'absolute',
                                    left: 20,
                                    top: 20,
                                    width: 18
                                }} />
                            </div>
                        </animated.div>
                        <img
                        className={'text-button'}
                        onClick={onToggleMobileMenu}
                        src={`images/close-button-dark.png`}
                        style={{
                            height: 25,
                            objectFit: 'contain',
                            position: 'absolute',
                            right: 20,
                            top: 20,
                            width: 25
                        }} />
                        <div style={{
                            bottom: 0,
                            padding: 24,
                            position: 'absolute',
                            textAlign: 'center',
                            width: '100%'
                        }}>
                            {getFooterComponents()}
                        </div>
                    </animated.div>
                </div>
            )
        }
        
        // fallback to rendering content for tablets and desktop 
        return (
            <div 
            ref={headerRef}
            style={{
                display: 'flex',
                flexDirection: 'column',
                padding: 16,
                pointerEvents: 'none',
                position: 'fixed',
                width: '100vw',
                zIndex: CalloutIndex
            }}>
                <animated.div style={{
                    alignItems: 'center',
                    backdropFilter: 'blur(40px)',
                    borderRadius: 12,
                    borderStyle: 'solid',
                    borderWidth: 2,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    padding: 24,
                    pointerEvents: 'auto',
                    textAlign: 'center',
                    WebkitBackdropFilter: 'blur(40px)',
                    width: '100%',
                    ...filterAnimations
                }}>
                    <animated.div 
                    className={'text-button'}
                    onClick={setNextActive.bind(this, { view: 'home' })}
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                        paddingLeft: 8,
                        position: 'relative',
                        width: 275,
                        ...logoAnimations
                    }}>
                        <img 
                        src={'images/logo-text-white.png'}
                        style={{
                            height: 25,
                            width: 'auto'
                        }} />
                    </animated.div>
                    <animated.div style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        position: 'relative',
                        ...menuAnimations
                    }}>
                        {items.map((item, index) => {
                            if(item.key === 'user') {
                                return null;
                            }
                            return (
                                <div 
                                key={index}
                                style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    marginRight: 32,
                                    position: 'relative'
                                }}>
                                    <span 
                                    className={'text-button'}
                                    onClick={item.onClick}
                                    onMouseEnter={evt => {
                                        setSelectedNavigationItem({
                                            key: item.key,
                                            target: evt.target
                                        });
                                    }}
                                    style={{
                                        ...Appearance.textStyles.title(),
                                        color: item.key === active.view ? Appearance.colors.primary() : 'white',
                                        fontFamily: 'Zen Dots',
                                    }}>{item.title.toUpperCase()}</span>
                                </div>
                            )
                        })}
                    </animated.div>
                    <div style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        justifySelf: 'flex-end',
                        justifyContent: 'flex-end',
                        position: 'relative',
                        width: 275
                    }}>
                        {getAccountComponents()}
                    </div>
                </animated.div>
                {getGlobalBanners()}
                {getNavigationComponents()}
                {getDesktopNavigationMenu()}
            </div>
        )
    }

    const getMainContent = () => {

        // prepare targets for selected view and filter out non-visible entries 
        let targets = (panels[active.view] || []).filter(target => {
            if(typeof(target.visible) === 'function') {
                return target.visible(user);
            }
            if(target.focus) {
                return target.focus === requestRef.current?.focus;
            }
            return target.visible !== false;
        });

        return (
            <animated.div 
            id={'content-container'}
            style={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                width: '100%',
                ...contentAnimations
            }}>
                {getLayers()}
                <div 
                onClick={onSetAccountDrawerExpandedValue.bind(this, false)}
                onMouseEnter={onMouseEnterMainContent}
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    width: '100%'
                }}>
                    <div style={{
                        ...getPanelPadding(),
                        position: 'relative',
                        width: '100%'
                    }}>
                        {targets.map(({ Component }, index) => (
                            <Component
                            index={index} 
                            key={index}
                            utils={utils}/>
                        ))}
                    </div>
                </div>
                {getHeader()}
                <RequiemChat utils={utils} />
            </animated.div>
        )
    }

    const getNavigationComponents = () => {

        // prepare list of items for key
        let items = [];
        switch(selectedNavigationItem?.key) {
            case 'resources':
            items = getResourceItems();
            break;

            case 'services':
            items = getServicesItems();
            break;

            case 'user':
            items = getUserItems();
            break;
        }

        // return items list if mobile device is detected
        if(Utils.isMobile() === true) {
            return items.map((entry, index) => {
                return (
                    <span 
                    className={'text-button'}
                    key={index}
                    onClick={entry.onClick || setNextActive.bind(this, { view: entry.key })}
                    style={{
                        ...Appearance.textStyles.layerItemTitle(),
                        color: 'white',
                        fontFamily: 'Zen Dots',
                        fontSize: 24,
                        pointerEvents: 'auto',
                        textAlign: 'center',
                        whiteSpace: 'normal'
                    }}>{entry.title.toUpperCase()}</span>
                )
            });
        }

        // determine location top and left for selected navigation item target
        let targetRect = selectedNavigationItem?.target?.getBoundingClientRect();

        // return desktop version of navigation items
        return items.length > 0 && (
            <div style={{
                left: targetRect?.left,
                minWidth: 250,
                overflow: 'hidden',
                paddingTop: 12,
                pointerEvents: 'auto',
                position: 'absolute',
                top: targetRect?.bottom + 28,
                zIndex: CalloutIndex
            }}>
                <animated.div style={{
                    backdropFilter: 'blur(40px)',
                    backgroundColor: filterAnimations.backgroundColor,
                    borderRadius: 12,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    textAlign: 'left',
                    WebkitBackdropFilter: 'blur(40px)',
                    width: '100%'
                }}>
                    {items.map((item, index) => {
                        return (
                            <div 
                            className={`view-entry-transparent`}
                            key={index}
                            onClick={() => {
                                setNextActive({ view: item.key });
                                setSelectedNavigationItem(false);
                            }}
                            style={{
                                alignItems: 'center',
                                borderBottom: index !== items.length - 1 && '1px solid rgba(255,255,255,0.15)',
                                display: 'flex',
                                flexDirection: 'row',
                                padding: 16,
                                width: '100%' 
                            }}>
                                <img 
                                src={window.theme === 'dark' ? 'images/asterick-badge-dark.png' : 'images/asterick-badge-light.png'}
                                style={{
                                    height: 25,
                                    marginRight: 12,
                                    objectFit: 'contain',
                                    width: 25 
                                }} />
                                <span style={{
                                    ...Appearance.textStyles.title(),
                                    color: 'white',
                                    fontFamily: 'Zen Dots',
                                    paddingRight: 8
                                }}>{item.title.toUpperCase()}</span>
                            </div>
                        )
                    })}
                </animated.div>
            </div>
        );
    }

    const getPanelPadding = () => {
        if(active.view === 'home' || active.view?.includes('services') === true) {
            return {
                paddingBottom: 128
            }
        }
        if(Utils.isMobile() === true) {
            return {
                paddingBottom: 128,
                paddingTop: (headerRef.current?.clientHeight || 0)
            }
        }
        return {
            paddingBottom: 128,
            paddingLeft: Appearance.styles.panelPadding(),
            paddingRight: Appearance.styles.panelPadding(),
            paddingTop: (headerRef.current?.clientHeight || 0) + 24
        }
    }

    const getResourceItems = () => {
        return [{
            key: 'affiliates',
            title: 'Affiliates'
        },{
            key: 'digital_life_checkup',
            title: 'Digital Life Checkup'
        },{
            key: 'faq',
            title: 'Frequently Asked Questions'
        },{
            key: 'knowledge_base',
            title: 'Knowledge Base'
        },{
            key: 'legal',
            title: 'Legal'
        },{
            key: 'sales_ops',
            title: 'Sales Operatives'
        }];
    }

    const getServicesItems = () => {
        return [{
            key: 'services_emergency_trigger',
            title: 'Emergency Trigger'
        },{
            key: 'services_instructions',
            title: 'Instructions'
        },{
            key: 'services_life_checks',
            title: 'LifeChecks'
        },{
            key: 'services_linked_accounts',
            title: 'Linked Accounts'
        },{
            key: 'services_requiem_ai',
            title: 'Requiem AI'
        },{
            key: 'services_data_vault',
            title: 'Secure Data Vault'
        }];
    }
    
    const connectToSockets = () => {
        let client = new WebSocketManager();
        client.connect({ user, url: API.sockets });
        setSockets(client);
    }

    const presentLoginWindow = (options = {}, callback) => {
        utils.layer.open({
            Component: Login.bind(this, { 
                onLogin: (user, options) => {

                    // pass result to login handler
                    onLogin(user, options);

                    // notify user of successful login
                    if(typeof(callback) === 'function') {
                        callback(user);
                    }
                },
                ...options
            }),
            id: 'login'
        });
    }

    const processLogoutRequest = (options = {}) => {

        let { redirect = true, remove_refresh_token = true } = options;

        // remove refresh token from storage and update user state
        if(remove_refresh_token === true) {
            localStorage.removeItem('user.refresh_token');
        }

        // close all layers
        setLayers([]);
        setLayerIndex([]);

        // remove user from local state
        userRef.current = null;
        setUser(null);

        // redirect to home page if applicable
        if(redirect === true) {
            setNextActive({ view: 'home' });
        }
    }

    const processSecureAuthRequest = async ({ access_token, origin_formatted, private_token, route }) => {

        // determine if a login access token was provided for the account currently logged in
        if(access_token) {
            confirmSecureAuthRequest({ access_token, origin_formatted, private_token });
            return;
        }

        // determine if an account signup was requested
        if(route === 'signup') {
            utils.layer.open({
                abstract: Abstract.create({
                    object: User.new(),
                    type: 'users'
                }),
                Component: AddEditUser.bind(this, {
                    isNewTarget: true,
                    onComplete: user => {
                        confirmSecureAuthRequest({ access_token: user?.token, origin_formatted, private_token });
                    }                
                }),
                id: 'new_user'
            });
            return;
        } 
        
        // show login layer and attach private token
        utils.user.request.login({}, user => {
            confirmSecureAuthRequest({ access_token: user?.token, origin_formatted, private_token });
        });
    }

    const confirmSecureAuthRequest = async ({ access_token, origin_formatted, private_token }) => {
        try {

            // submit access token for account and private token for request to server
            await Request.post(utils, '/users/', {
                access_token: access_token,
                private_token: private_token,
                type: 'confirm_secure_auth_request'
            });

            // show cofirmation alert to user
            utils.banner.show({
                title: 'All Done!',
                message: `Your secure session with LifeWipe is now active. You can return to ${origin_formatted} whenever you’re ready.`,
            });
            
        } catch(e) {
            utils.alert.show({
                error: e,
                message: `There was an issue completing your secure authentication request. ${e.message || 'An unknown error occurred.'}`,
                title: 'Oops!'
            });
        }
    }

    const processAnalyticsMetadataFromQueryParameters = parameters => {

        // no further processing is required if parameters originated from a first party resource
        if(parameters.utm_campaign === 'draft_outreach') {
            utils.analytics.log('draft_outreach_visit', {
                utm_campaign: parameters.utm_campaign,
                utm_source: parameters.utm_source,
                utm_medium: parameters.utm_medium
            });
            return;
        }

        // prepare keys to generate a metadata object from query parameters
        const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid', 'gclid', 'msclkid', 'ttclid'];
        const metadata = keys.reduce((object, key) => {

            // add parameter to metadata object if it exists in query parameters
            if(parameters[key]) {
                object[key] = parameters[key];
            }
            return object;
        }, {});
        
        // update advertising metadata if any relevant parameters were found
        if(Object.keys(metadata).length > 0) {
            setAdvertisingMetadata({ ...metadata, timestamp: moment().unix() });
        }
    }

    const setupFirebase = () => {

        // initialize firebase app
        const app = initializeApp({
            appId: "1:282973534294:web:3bff6cce2a6abd300b23f7",
            apiKey: "AIzaSyBbqsoy0M58q4HRBS2H4Gybka5uuWswVUY",
            projectId: "lifewipe-inc-b7798"
        });

        // get analytics instance
        const analyticsInstance = getAnalytics(app);
        analyticsRef.current = analyticsInstance;
        
        // get or create anonymous id for users who are not logged in
        let anonId = localStorage.getItem('analytics_anonymous_id');
        if(!anonId) {
            anonId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem('analytics_anonymous_id', anonId);
        }
        setUserId(analyticsInstance, anonId);
    }

    const setLeadLandingProps = () => {

        let { code, focus, provider } = requestRef.current || {};

        // determine if a referral code was provided
        if(code) {
            console.log(`referred by user code set to ${code}`);
            setReferredByUserCode(code);
        }

        // determine which route to use
        setNextActive({ 
            focus: focus,
            provider: provider,
            view: 'lead_landing'
        });

        // update state for request to user flags downstream
        setRequest(requestRef.current || {});
    }

    const setNextActive = (props, options = {}) => {

        // no further logic is required if no view was provided
        if(!props || !props.view) {
            return;
        }

        // determine if a walkthrough was requested
        if(props.view === 'onboarding') {
            onSetOnboardingVisibility(true);
            return;
        }

        // determine if a digital life checkup quiz was requested
        if(props.view === 'digital_life_checkup') {
            utils.layer.open({
                Component: DigitalLifeCheckup,
                id: 'digital_life_checkup'
            });
            return;
        }

        // update local state with active props
        setActive(props);

        // prepare path components for url
        let path = null;
        if(options?.should_update_url_components !== false) {
            let components = QueryString.parse(window.location.search);
            if(Object.keys(components).length > 1) {
                path = window.location.search;
            } else {
                path = props.view === 'home' ? '/' : `/?route=${props.view}`;
            }
        }

        // update history states with new view
        let title = getDocumentTitleForView(props.view);
        window.history.pushState(props, title, path);
    }

    const setRequestedReferralCode = async ({ code, redirect, show_confirmation_banner }) => {
        try {

            // update local state with referral code and direct user to pricing page
            console.log(`referred by user code set to ${code}`);
            setReferredByUserCode(code);
            setNextActive({ view: redirect || 'pricing' });

            // update history state by removing the query parameters
            window.history.pushState(null, 'LifeWipe Inc.', '/');

            // submit request to verify the referral code
            await Request.get(utils, '/users/', {
                referral_code: code,
                type: 'verify_referral_code'
            });

            // show banner indicating that the referral code has been set
            if(parseInt(show_confirmation_banner) !== 0) {
                utils.banner.show({
                    icon: 'images/banner-icon-checkmark.png',
                    message: `The referral is now locked in. We'll automatically apply it for you during signup—no need to lift a finger.`,
                    title: 'Referral Code Accepted'
                });
            }

        } catch(e) {
            utils.banner.show({
                icon: 'images/red-x-icon.png',
                message: e.message,
                title: 'Invalid Referral Code'
            });
        }
    }

    const setupStructure = async () => {
        try {

            // set window size for system
            onUpdateWindowSize();

            // initialize firebase analytics
            setupFirebase();

            // initialize stripe sdk
            stripeRef.current = await loadStripe(API.stripe);

            // amimate components into view, lol
            setContentAnimations({ opacity: 1, top: 0 });
            setLogoAnimations({ left: 0, opacity: 1 });
            setMenuAnimations({ opacity: 1, right: 0 });

            // update request state with query parameters
            let parameters = QueryString.parse(window.location.search, { parseArrays: false });
            requestRef.current = parameters;

            // capture advertising and click-through metadata from query parameters
            processAnalyticsMetadataFromQueryParameters(parameters);

            // set default active view, lifechecks require a manual login every time
            switch(parameters.route) {
                case 'cyber_finders_application':
                case 'sales_ops_application':
                setNextActive({ view: 'sales_ops' }, { should_update_url_components: false });
                break;

                case 'digital_life_checkup':
                parameters.code ? setRequestedReferralCode({ ...parameters, redirect: 'home' }) : setNextActive({ view: 'home' });
                onShowCheckupQuiz();
                break;

                case 'about':
                case 'contact':
                case 'faq':
                case 'knowledge_base':
                case 'legal':
                case 'partnerships':
                case 'pricing':
                case 'sales_ops':
                case 'services_data_vault':
                case 'services_emergency_trigger':
                case 'services_instructions':
                case 'services_life_checks':
                case 'services_linked_accounts':
                case 'services_requiem_ai':
                setNextActive({ 
                    view: parameters.route, 
                    waypoint: parameters.waypoint 
                });
                break;

                case 'lead_landing':
                case 'lead_landing_urgency':
                requestRef.current = { 
                    ...requestRef.current, 
                    auto_starter_plan: parameters.auto_starter_plan == 1,
                    mode: parameters.mode
                };
                setLeadLandingProps();
                break;

                case 'lifechecks':
                setNextActive({ 
                    view: parameters.route, 
                    waypoint: parameters.waypoint 
                },{
                    should_update_url_components: false
                });
                break;

                case 'privacy':
                case 'terms_of_service':
                setNextActive({ view: 'legal', waypoint: parameters.route });
                break;

                case 'referrals':
                setRequestedReferralCode(parameters);
                break;

                case 'secure_portal':
                setNextActive({ view: parameters.route });
                setRequest({ token: requestRef.current?.token });
                break;

                case 'support':
                setNextActive({ view: 'contact' });
                break;
            }

            // remove query parameters from the query string
            window.history.pushState(null, 'LifeWipe Inc.');

            // prepare auto login token for saved sessions
            let token = parameters.login_token || localStorage.getItem('user.refresh_token');

            // determine if an action was requested through query parameters and should be executed prior to login
            let { shouldAutoLogin = true } = onRunGeneralQueryActions() || {};

            // determine if additional logic is required
            if(token && shouldAutoLogin === true) {
                onLoginWithRefreshToken(token);
            }

            // run post login query actions early if no refresh token was found
            !token && onRunUserQueryActions();

        } catch(e) {
            console.error(e.message);
        }
    }

    const validateTrustedDevice = userID => {
        return new Promise(async (resolve, reject) => {
            try {

                // retrieve trusted device token from storage
                let token = requestRef.current?.trusted_device_token || utils.user.device.get(userID);

                // validate token if a token was found or start a new device registration otherwise
                token ? await validateTrustedDeviceToken(utils, { token, user_id: userID }) : await runTrustedDeviceRegistration(utils, userID);
                resolve({ token });
                
            } catch(e) {
                setTimeout(reject.bind(this, e), 500);
            }
        });
    }

    const viewRequiresDarkTheme = () => {
        return active.view === 'home' || active.view?.includes('services');
    }

    const utils = {
        alert: {
            show: props => {

                // prepare buttons array 
                let buttons = props.buttons || [{
                    key: 'cancel',
                    title: 'Okay', 
                    style: 'cancel'
                }];

                // add subscription button if applicable 
                if(props.error?.code === Request.status.get().subscription_required) {
                    buttons = [{
                        key: 'new_subscription',
                        title: 'Explore Plans', 
                        style: 'default'
                    },{
                        key: 'cancel',
                        title: 'Close', 
                        style: 'cancel'
                    }];
                }

                // add upgrade subscription button if applicable 
                if(props.error?.code === Request.status.get().subscription_upgrade_required) {
                    buttons = [{
                        key: 'upgrade_subscription',
                        title: 'Upgrade Subscription', 
                        style: 'default'
                    },{
                        key: 'cancel',
                        title: 'Close', 
                        style: 'cancel'
                    }];
                }

                // update local state with new alert props
                setAlerts(alerts => {
                    return update(alerts, {
                        $unshift: [{
                            id: `${moment().unix()}-${Math.random()}`,
                            ...props,
                            buttons: buttons,
                            onClick: key => {
                                if(key === 'new_subscription' || key === 'upgrade_subscription') {
                                    onShowSubscriptionOptions();
                                    return;
                                }
                                if(typeof(props.onClick) === 'function') {
                                    props.onClick(key);
                                    return;
                                }
                            }
                        }]
                    });
                });
            },
            dev: () => {
                setAlerts(alerts => {
                    return update(alerts, {
                        $push: [{
                            id: `${moment().unix()}-${Math.random()}`,
                            title: 'In Development',
                            message: 'This feature is currently under development and will become available at a later date'
                        }]
                    });
                });
            },
            error: props => {

                // log error with firebase
                utils.analytics.log('exception', {
                    description: props.message,
                    error: props.error.message,
                    fatal: false,
                    origin: props.origin
                });
                
                // show error alert with details about exception
                return utils.alert.show({
                    title: 'Oops!',
                    ...props
                });
            }
        },
        animations: {
            get: key => {
                switch(key) {
                    case 'panel':
                    return {
                        animateIn: {
                            top: 0,
                            opacity: 1
                        },
                        animateOut: {
                            top: -50,
                            opacity: 0
                        },
                        config: {
                            friction: 10
                        },
                        delay: 0.5
                    }

                    default:
                    return null;
                }
            }
        },
        analytics: {
            log: (eventName, params = {}) => {
                if(API.staging === true) {
                    console.warn('Analytics events are disabled in staging environments.', eventName, params);
                    return;
                }
                if(analyticsRef.current) {
                    logEvent(analyticsRef.current, eventName, params);
                }
            }
        },
        api: {
            headers: () => {
                return {
                    'Content-Type': 'application/json',
                    'X-API': `Version ${API.version}`,
                    'X-Timezone': `TZ ${moment.tz.guess()}`,
                    'X-Web': `Build ${API.build}`,
                    ...deviceTokenRef.current && {
                        'X-Device': `Token ${deviceTokenRef.current}`  
                    },
                    ...userRef.current && {
                        'Authorization': `Bearer ${userRef.current.token}`,
                        'Identification': `User ${userRef.current.user_id}`
                    },
                    ...partnership && {
                        'X-Partnership': `ID ${partnership.id}`,
                        'X-Partnered-User': `ID ${partnership.user?.user_id}`
                    }
                }
            }
        },
        banner: {
            show: props => {
                setBanners(banners => {
                    return update(banners, {
                        $push: [{
                            id: `${moment().unix()}-${Math.random()}`,
                            ...props
                        }]
                    });
                });
            }
        },
        content: ContentManager,
        device: {
            get: () => ({
                name: osName && browserName ? `${browserName} ${osName}` : (browserName || 'Unknown browser type'),
                platform: 'web',
                token: deviceTokenRef.current,
                verified: deviceTokenRef.current ? true : false
            })
        },
        events: EventsManager,
        large_alert: {
            show: setLargeAlert 
        },
        layer: {
            open: layer => onOpenLayer(layer),
            close: layer => onCloseLayer(layer),
            requestClose: id => {
                let closeEvent = new CustomEvent('onLayerAction', {
                    detail: {
                        action: 'close',
                        layerID: id
                    }
                });
                window.dispatchEvent(closeEvent);
            }
        },
        navigation: {
            get: () => active,
            set: setNextActive
        },
        sheet: {
            show: (sheet, callback) => {
                setSheet({ ...sheet, onClick: callback })
            }
        },
        stripe: stripeRef.current,
        user: {
            capabilities: {
                verify: (namespace, action) => {
                    return new Promise((resolve, reject) => {

                        // require that users have verified their identity before creating an account
                        if(['accounts', 'data_vault', 'emergency_triggers', 'life_checks'].includes(namespace) && user.verified === false) {

                            // show an alert to prompt the user to select a plan
                            utils.alert.show({
                                title: 'Verify Identity',
                                message: `Before you can get things setup, we need to verify your identity. This protects your data, your legacy, and your system permissions.`,
                                buttons: [{
                                    key: 'confirm',
                                    title: 'Get Started',
                                    style: 'default'
                                },{
                                    key: 'cancel',
                                    title: 'Cancel',
                                    style: 'cancel'
                                }],
                                onClick: key => {
                                    if(key === 'confirm') {
                                        onVerifyIdentity();
                                        return;
                                    }
                                }
                            });

                            // throw an error to exit the promise
                            let error = new Error(`missing_subscription_plan`);
                            reject(error);
                            return;
                        }

                        // no further logic is required if no subscription has been added
                        if(!user.subscription) {

                            // show an alert to prompt the user to select a plan
                            utils.alert.show({
                                title: 'Activate Your Access',
                                message: `Unlock this feature by choosing a subscription plan. Try it free now — no credit card required.`,
                                buttons: [{
                                    key: 'pricing',
                                    title: 'Explore Plans',
                                    style: 'default'
                                },{
                                    key: 'cancel',
                                    title: 'Close',
                                    style: 'cancel'
                                }],
                                onClick: key => {
                                    if(key === 'pricing') {
                                        onShowSubscriptionOptions();
                                        return;
                                    }
                                }
                            });

                            // throw an error to exit the promise
                            let error = new Error(`missing_subscription_plan`);
                            reject(error);
                            return;
                        }
                        
                        // TODO => validate namespace and action against subscription tiers
                        resolve();
                    });
                }
            },
            device: {
                get: userID => {
                    let result = localStorage.getItem(`${userID}.trusted_device_token`);
                    return result !== 'null' ? result : null;
                },
                set: (userID, token) => {
                    if(token && token !== 'null') {
                        localStorage.setItem(`${userID}.trusted_device_token`, token);
                    }
                    deviceTokenRef.current = token;
                    setDeviceToken(deviceTokenRef.current);
                },
                remove_current_token: () => {
                    let keys = Object.keys(localStorage);
                    keys.forEach(key => {
                        if(deviceTokenRef.current && key.includes(deviceTokenRef.current) === true) {
                            localStorage.removeItem(key);
                        }
                    });
                }
            },
            get: () => userRef.current,
            logout: props => processLogoutRequest({ remove_refresh_token: false, ...props }),
            request: {
                login: presentLoginWindow,
                signup: props => {

                    let { onComplete } = props || {};

                    // open layer to start user creation flow
                    utils.layer.open({
                        abstract: Abstract.create({
                            object: User.new(),
                            type: 'users'
                        }),
                        Component: AddEditUser.bind(this, {
                            isNewTarget: true,
                            onComplete: (user, options) => {

                                // write login token to local storage
                                if(options.remember_me === true) {
                                    localStorage.setItem('user.refresh_token', user.refresh_token);
                                }
                        
                                // update local storage and state with trusted device token for future requests
                                utils.user.device.set(user.user_id, options.trusted_device_token);
                        
                                // update local state with user object and determine if an action was requested through query parameters and should be executed post-login
                                let target = User.create(user);
                                userRef.current = target;
                                setUser(target);

                                // call onComplete callback if provided
                                if(typeof(onComplete) === 'function') {
                                    onComplete(user);
                                }
                            }
                        }),
                        id: 'new_user'
                    });
                }
            },
            set: onLogin
        }
    }
    
    useEffect(() => {
        setSelectedNavigationItem(false);
    }, [accountDrawerExpanded]);

    useEffect(() => {
        onActiveChange();
    }, [active]);

    useEffect(() => {
        deviceTokenRef.current = deviceToken;
    }, [deviceToken]);

    useEffect(() => {
        layersRef.current = layers;
    }, [layers]);

    useEffect(() => {
        if(Utils.isMobile() === false) {
            setMenuDrawerAnimations({ left: system.window.size.width });
            return;
        }
        setMenuDrawerAnimations({ left: menuExpanded ? 0 : system.window.size.width });
    }, [menuExpanded, system.window.size]);

    useEffect(() => {
        if(Utils.isMobile() === true) {
            onSelectedNavigationItemChange();
        }
    }, [selectedNavigationItem]);

    useEffect(() => {
        requestRef.current = request || {};
    }, [request]);

    useEffect(() => {
        userRef.current = user;
        if(user) {
            onRunUserQueryActions();
            onConnectToUserSockets();
            analyticsRef.current && setUserId(analyticsRef.current, user.user_id);
        }
    }, [user]);

    useEffect(() => {

        setupStructure();
        connectToSockets();

        // setup window listeners
        window.addEventListener('resize', onUpdateWindowSize);
        window.addEventListener('beforeunload', evt => {
            evt.returnValue = null;
            return null;
        });
        window.addEventListener('popstate', evt => {
            let view = evt.state?.view || 'home';
            setActive({ view });
        });

        // css variables
        document.documentElement.style.setProperty('--text', Appearance.colors.text());
        document.documentElement.style.setProperty('--textfield', Appearance.colors.textField());
        document.documentElement.style.setProperty('--soft_border', Appearance.colors.softBorder());

        // theme and theme listeners
        window.theme = 'dark';
        document.documentElement.style.setProperty('--theme', window.theme);
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', onUpdateTheme);
        onSetStyleSheetProperties();

    }, []);

    return (
        <div
        className={`root-container ${theme}`}
        nonce={nonce}
        style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100dvh',
            position: 'relative',
            width: system.window.size.width
        }}>
            {getMainContent()}
            {getDesktopVersionDetails()}
            {onboarding.visible === true && (
                <Onboarding 
                onClose={onSetOnboardingVisibility.bind(this, false)}
                utils={utils}/>
            )}
            {sheet && (
                <Sheet
                {...sheet}
                onClose={setSheet.bind(this, null)}/>
            )}
            {largeAlert && (
                <LargeAlert
                {...largeAlert}
                onClose={setSheet.bind(this, null)}
                utils={utils} />
            )}
            <BannerStack 
            banners={banners}
            onClose={id => setBanners(banners => banners.filter(banner => banner.id !== id))}
            utils={utils} />
            <AlertStack 
            alerts={alerts}
            onClose={setAlerts}
            utils={utils} />
        </div>
    )
}

export default App;
